/**
 * Sawyer 08 meds-feeding — client for the staff meds & feeding surfaces: the
 * profile editor (G3), the Meds & feeding report (A1 / G10) and the med
 * pass (A2). Its own module so `lib/api.ts` (touched by most Sawyer lanes)
 * only gains optional type fields. Every call goes through the shared
 * `request` wrapper (auth, cold-start retry, humanized errors) and returns
 * `{ data } | { error, details }`.
 *
 * Deploy-window rule (lead decision 9): frontends auto-deploy before the
 * server. Every field read from the server that this lane added is optional
 * here and the pages render safely without it.
 */
import { api, request, type ApiResponse, type AteNoneWarning } from './api.js';

export type SlotSource = 'set' | 'frequency' | 'frequency_default' | 'unscheduled';

export interface StaffMedicationView {
  id: string;
  name: string;
  dosage: string | null;
  frequency: string | null;
  instructions: string | null;
  slots: string[];
  effectiveSlots: string[];
  slotSource: SlotSource;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
}

export interface MedicationInput {
  name: string;
  dosage?: string | null;
  frequency?: string | null;
  instructions?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  slots?: string[];
}

export interface FeedingInput {
  feedingNotes?: string | null;
  foodType?: string | null;
  feedingMethod?: string | null;
  feedingSlots?: string[];
  medicationNotes?: string | null;
}

export interface FeedingResult {
  feeding: { notes: string | null; foodType: string | null; method: string | null; feedingSlots?: string[] };
  medicationNotes: string | null;
}

/** One field-level problem from the server's `details`. */
export interface FieldIssue {
  field: string;
  message: string;
}

export function fieldIssues(details: unknown): FieldIssue[] {
  if (!Array.isArray(details)) return [];
  return details.filter(
    (d): d is FieldIssue =>
      typeof d === 'object' && d !== null && typeof (d as FieldIssue).field === 'string' &&
      typeof (d as FieldIssue).message === 'string',
  );
}

const enc = encodeURIComponent;

export function createMedication(apiUrl: string, dogId: string, body: MedicationInput) {
  return api.post<{ medication: StaffMedicationView }>(apiUrl, `/api/admin/dogs/${enc(dogId)}/medications`, body);
}

export function updateMedication(apiUrl: string, dogId: string, medicationId: string, body: Partial<MedicationInput>) {
  return api.patch<{ medication: StaffMedicationView }>(
    apiUrl,
    `/api/admin/dogs/${enc(dogId)}/medications/${enc(medicationId)}`,
    body,
  );
}

export function stopMedication(apiUrl: string, dogId: string, medicationId: string) {
  return api.post<{ medication: StaffMedicationView }>(
    apiUrl,
    `/api/admin/dogs/${enc(dogId)}/medications/${enc(medicationId)}/stop`,
    {},
  );
}

export function deleteMedication(apiUrl: string, dogId: string, medicationId: string) {
  return api.delete<{ deleted: true }>(apiUrl, `/api/admin/dogs/${enc(dogId)}/medications/${enc(medicationId)}`);
}

export function updateFeeding(apiUrl: string, dogId: string, body: FeedingInput) {
  return api.patch<FeedingResult>(apiUrl, `/api/admin/dogs/${enc(dogId)}/feeding`, body);
}

// ── The day read (report + med pass) ────────────────────────────────────

export type CareDayService = 'all' | 'boarding' | 'daycare';
export type VisitStatus = 'expected' | 'checked_in' | 'checked_out';
export type MealSource = 'set' | 'house_default' | 'meal_add_on';

export interface DoseStatusView {
  latest: { outcome: string; staffName: string | null; occurredAt: string } | null;
  history: Array<{ id: string; outcome: string; staffName: string | null; occurredAt: string; notes: string | null }>;
  countToday: number;
}

export interface CareDayDog {
  dogId: string;
  name: string;
  breed: string | null;
  photoUrl: string | null;
  ownerName: string | null;
  bookingId: string;
  service: 'boarding' | 'daycare';
  visitStatus: VisitStatus;
  arrivesToday: boolean;
  leavesToday: boolean;
  checkedOutAt: string | null;
  allergies: string | null;
  medicationNotes: string | null;
  feeding: {
    notes: string | null;
    notesSource: 'stay' | 'dog' | null;
    foodType: string | null;
    method: string | null;
    feedingSlots: string[];
    lunchOverride: boolean | null;
    eatsLunch: boolean;
  };
  /** The dog's own values before this stay's overrides (seed for the editor). */
  profile?: { feedingNotes: string | null; medicationNotes: string | null };
  medications: StaffMedicationView[];
  /** Sawyer #1295 — the meals this dog ate none of on the day; absent on an older server. */
  ateNone?: AteNoneWarning | null;
}

export interface CareDayEntry {
  dogId: string;
  meal: { meal: 'breakfast' | 'lunch' | 'dinner'; source: MealSource } | null;
  doses: Array<{ medicationId: string; status: DoseStatusView }>;
}

export interface CareDayGroup {
  slot: 'am' | 'midday' | 'pm' | 'bedtime' | 'as_needed';
  label: string;
  entries: CareDayEntry[];
}

export interface CareDayResponse {
  date: string;
  timeZone: string;
  service: CareDayService;
  facilityName: string | null;
  generatedAt: string;
  isToday: boolean;
  truncated: boolean;
  canEditMedsFeeding: boolean;
  canLogDoses: boolean;
  canSetLunch: boolean;
  canOpenProfiles: boolean;
  dogs: CareDayDog[];
  groups: CareDayGroup[];
  notesOnly: string[];
  unscheduledMeds: Array<{ dogId: string; medicationId: string }>;
  /**
   * Medications given at home on this visit (a daycare dog's AM / PM dose).
   * Optional: a server from before review round 1 does not send it (and
   * put those medications under unscheduledMeds instead).
   */
  atHomeMeds?: Array<{ dogId: string; medicationId: string; slots: string[] }>;
  counts: { dogs: number; boarding: number; daycare: number; dosesDue: number; dosesRecorded: number };
}

export function fetchCareDay(
  apiUrl: string,
  date: string | null,
  service: CareDayService,
  serviceTypeId?: string | null,
): Promise<ApiResponse<CareDayResponse>> {
  const q = new URLSearchParams();
  if (date) q.set('date', date);
  q.set('service', service);
  if (serviceTypeId) q.set('serviceTypeId', serviceTypeId);
  return api.get<CareDayResponse>(apiUrl, `/api/admin/care/day?${q.toString()}`);
}

export type MedPassOutcome = 'prepared' | 'given' | 'partially_administered' | 'refused';

export interface DoseInput {
  dogId: string;
  medicationId: string;
  slot: string;
  outcome: MedPassOutcome;
  bookingId?: string;
  notes?: string;
}

/**
 * One med-pass tap. NO automatic retry (maxRetries 0): a dose is a legal
 * administration record, and replaying a POST whose first attempt may have
 * landed would record the dose twice. A failure comes straight back to the
 * row, which says so and lets the person tap again.
 */
export function appendDose(apiUrl: string, body: DoseInput) {
  return request<{ dose: { dogId: string; medicationId: string; slot: string; status: DoseStatusView | null } }>(
    apiUrl,
    '/api/admin/care/doses',
    { method: 'POST', body: JSON.stringify(body) },
    { maxRetries: 0 },
  );
}

export function setVisitLunch(apiUrl: string, bookingId: string, dogId: string, lunch: boolean | null) {
  return api.put<{ bookingId: string; dogId: string; lunchOverride: boolean | null; eatsLunch: boolean }>(
    apiUrl,
    `/api/admin/care/visits/${enc(bookingId)}/dogs/${enc(dogId)}/lunch`,
    { lunch },
  );
}
